'use client';

import { useCallback } from 'react';
import { cn, truncateAddress } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { AvatarWithFlair, LevelBadge } from '@/components/prelaunch/avatar-with-flair';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';

export type IdentityInfo = { displayName: string | null; avatarUrl: string; level: number };

export function resolveIdentity(
  address: string,
  eventIdentity: { displayName?: string; avatarUrl?: string; level?: number } | undefined,
  batch: Record<string, IdentityInfo>,
): { displayName: string | null; avatarUrl: string | undefined; level: number } {
  if (eventIdentity?.displayName || eventIdentity?.avatarUrl) {
    return {
      displayName: eventIdentity.displayName ?? null,
      avatarUrl: eventIdentity.avatarUrl,
      level: eventIdentity.level ?? 0,
    };
  }
  const b = batch[address.toLowerCase()];
  if (b) {
    return { displayName: b.displayName, avatarUrl: b.avatarUrl, level: b.level ?? 0 };
  }
  return { displayName: null, avatarUrl: undefined, level: 0 };
}

export function AvatarTooltip({
  address,
  identity,
  batchIdentities,
  greyed,
  triggerSize = 'xs',
  /**
   * List/table rows: show the deterministic on-chain avatar immediately instead of
   * waiting on social URLs from `/api/users` (which can feel blank for seconds).
   * Tooltip still shows resolved name + photo when available.
   */
  instantThumbnail = false,
}: {
  address: string;
  identity?: { displayName?: string; avatarUrl?: string; level?: number };
  batchIdentities: Record<string, IdentityInfo>;
  greyed?: boolean;
  triggerSize?: 'xxs' | 'xs';
  instantThumbnail?: boolean;
}) {
  const id = resolveIdentity(address, identity, batchIdentities);
  /** Always use resolved URL when present so `/api/users` batch avatars show in feeds (instantThumbnail only affects load UX via `AvatarWithFlair`). */
  const thumbUrl = id.avatarUrl || undefined;
  const profileModal = useProfileModalOptional();
  const open = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      profileModal?.openProfile(address);
    },
    [profileModal, address],
  );
  // Outside the modal provider (e.g. partner SDK embed) we render a
  // plain figure — clicking nowhere is friendlier than a 404 from the
  // removed `/profile/[address]` route.
  const interactive = !!profileModal;
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        {interactive ? (
          <button
            type="button"
            onClick={open}
            className={cn('shrink-0 cursor-pointer', greyed && 'opacity-40')}
            aria-label={`Open profile for ${id.displayName || truncateAddress(address)}`}
          >
            <AvatarWithFlair avatarUrl={thumbUrl} address={address} level={id.level} size={triggerSize} priority={instantThumbnail} />
          </button>
        ) : (
          <span className={cn('shrink-0', greyed && 'opacity-40')}>
            <AvatarWithFlair avatarUrl={thumbUrl} address={address} level={id.level} size={triggerSize} priority={instantThumbnail} />
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent side="top" className="p-2">
        <div className="flex items-start gap-3 py-1 min-w-[180px]">
          <div className="flex flex-col items-center shrink-0">
            <AvatarWithFlair avatarUrl={id.avatarUrl} address={address} level={id.level} size="sm" />
          </div>
          <div className="flex-1 min-w-0">
            {interactive ? (
              <button
                type="button"
                onClick={open}
                className="font-medium truncate block hover:underline hover:text-primary text-left"
              >
                {id.displayName || truncateAddress(address)}
              </button>
            ) : (
              <span className="font-medium truncate block">
                {id.displayName || truncateAddress(address)}
              </span>
            )}
            {id.level > 0 && <LevelBadge level={id.level} className="text-[10px]" />}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
